import { Box, type BoxProps } from '@mantine/core'
import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  type TableHTMLAttributes,
} from 'react'

const T = {
  wrap: 'app-table-wrap',
  table: 'data-table',
  num: 'data-table__num',
  narrow: 'data-table__narrow',
  mono: 'data-table__mono',
  subject: 'data-table__subject',
  muted: 'data-table__muted',
  clip: 'data-table__clip',
  thRight: 'data-table__th--right',
  thCenter: 'data-table__th--center',
  tdRight: 'data-table__td--right',
  tdCenter: 'data-table__td--center',
} as const

function cx(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(' ')
}

/** Скролл, рамка и фон — оборачивайте `<table className={dataTableClass.table}>` или `<DataTable>` */
export const DataTableScroll = forwardRef<HTMLDivElement, BoxProps & { children?: ReactNode }>(
  function DataTableScroll({ className, children, ...props }, ref) {
    return (
      <Box ref={ref} component="div" className={cx(T.wrap, className)} {...props}>
        {children}
      </Box>
    )
  },
)

export const DataTable = forwardRef<HTMLTableElement, TableHTMLAttributes<HTMLTableElement>>(
  function DataTable({ className, ...props }, ref) {
    return <table ref={ref} className={cx(T.table, className)} {...props} />
  },
)

export const DataThead = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function DataThead({ className, ...props }, ref) {
  return <thead ref={ref} className={className} {...props} />
})

export const DataTbody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function DataTbody({ className, ...props }, ref) {
  return <tbody ref={ref} className={className} {...props} />
})

export const DataTr = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  function DataTr({ className, ...props }, ref) {
    return <tr ref={ref} className={className} {...props} />
  },
)

export type DataThProps = ThHTMLAttributes<HTMLTableCellElement> & {
  /** Узкая колонка № */
  variantNum?: boolean
  /** Узкая колонка, выравнивание вправо */
  variantNarrow?: boolean
  align?: 'left' | 'center' | 'right'
}

export const DataTh = forwardRef<HTMLTableCellElement, DataThProps>(function DataTh(
  { className, variantNum, variantNarrow, align = 'left', ...props },
  ref,
) {
  const alignClass =
    align === 'right' ? T.thRight : align === 'center' ? T.thCenter : undefined
  return (
    <th
      ref={ref}
      className={cx(variantNum && T.num, variantNarrow && T.narrow, alignClass, className)}
      {...props}
    />
  )
})

export type DataTdProps = TdHTMLAttributes<HTMLTableCellElement> & {
  mono?: boolean
  muted?: boolean
  clip?: boolean
  narrow?: boolean
  num?: boolean
  subject?: boolean
  align?: 'left' | 'center' | 'right'
}

export const DataTd = forwardRef<HTMLTableCellElement, DataTdProps>(function DataTd(
  { className, mono, muted, clip, narrow, num, subject, align = 'left', ...props },
  ref,
) {
  const alignClass =
    align === 'right' ? T.tdRight : align === 'center' ? T.tdCenter : undefined
  return (
    <td
      ref={ref}
      className={cx(
        mono && T.mono,
        muted && T.muted,
        clip && T.clip,
        narrow && T.narrow,
        num && T.num,
        subject && T.subject,
        alignClass,
        className,
      )}
      {...props}
    />
  )
})

/** Строка классов для ручной разметки без компонентов */
export const dataTableClass = {
  wrap: T.wrap,
  table: T.table,
  /** Список обращений: `table-layout: fixed`, доли колонок, скролл ниже min-width */
  ticketsList: 'data-table--tickets-list',
  ticketsTdAssignee: 'tickets-td--assignee',
  ticketsTdCustomer: 'tickets-td--customer',
  num: T.num,
  narrow: T.narrow,
  mono: T.mono,
  subject: T.subject,
  muted: T.muted,
  clip: T.clip,
  thRight: T.thRight,
  thCenter: T.thCenter,
  tdRight: T.tdRight,
  tdCenter: T.tdCenter,
} as const
